import React, { Fragment, useEffect, useState } from "react";
import {
  Select as AntSelect,
  Input,
  Form,
  Button,
  DatePicker,
  DatePickerProps,
  Select,
  Space,
} from "antd";
import {
  TextField,
  Box,
  FormControl,
  Button as MUIButton,
  CircularProgress,
} from "@mui/material";
import { CaretDownOutlined } from "@ant-design/icons";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import "./AuditCreation.scss";
import { toastMessage } from "../../helper";
import { createExternalAuditService } from "../../services/verificationService";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { Moment } from "moment";
import { EMAIL_REGEX } from "../../constants/regexConstants";
dayjs.extend(customParseFormat);

const AuditCreation = () => {
  const navigate = useNavigate();
  const { RangePicker } = DatePicker;
  const { state } = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [bunitsList, setBunitsList] = useState<any>([]);
  const [auditData, setAuditData] = useState<any>({
    auditorCompany: "",
    email: "",
    fromDate: null,
    toDate: null,
    businessUnits: "",
  });
  const [errorData, setErrorData] = useState({
    emailError: "",
  });
  const { emailError } = errorData;
  const monthFormat = "YYYY/MM";
  const [defaultDateRange, setDefaultRange] = useState<any>([]);

  const [startMonth, setStartMonth] = useState(
    new Date(new Date().getFullYear(), 0, 2)
  );
  const [endMonth, setEndMonth] = useState(
    new Date(new Date().getFullYear(), 11, 31)
  );
  type RangeValue = [Moment | null, Moment | null] | null;
  const [dates, setDates] = useState<RangeValue>(null);

  const onChangeDate = (e: any) => {
    let startDate = new Date(e[0].$d);
    startDate = new Date(startDate.getFullYear(), startDate.getMonth(), 2);
    setStartMonth(startDate);
    let endDate = new Date(e[1].$d);

    endDate = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 1);
    setEndMonth(endDate);
    setAuditData({
      ...auditData,
      fromDate: startDate,
      toDate: endDate,
    });
  };
  const onOpenChange = (open: boolean) => {
    if (open) {
      setDates([null, null]);
    } else {
      setDates(null);
    }
  };

  const handleChange = (value: any, key: any) => {
    if (key === "email") {
      setErrorData({ ...errorData, emailError: "" });
      //regex mail
      if (!EMAIL_REGEX.test(value)) {
        setErrorData({ ...errorData, emailError: "* Invalid Email" });
      }
    }
    setAuditData({ ...auditData, [key]: value });
  };
  const onChangeBunit = (e: any, target: string) => {
    let newIds: any = e?.map((each: any) => {
      let data: any = bunitsList?.filter((curr: any) => curr.value === each);
      return data[0]?.value;
    });
    let businessUnitsString = newIds.join(",");
    setAuditData({ ...auditData, businessUnits: businessUnitsString });
  };

  const handleSubmit = () => {
    setIsLoading(true);
    createExternalAuditService(auditData)
      .then((res: any) => {
        toastMessage("Audit Created Successfully", "success");
        navigate("/audits");
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCancel = () => {
    navigate("/audits");
  };

  const isValidForm = () => {
    return (
      auditData?.auditorCompany &&
      auditData?.email &&
      !emailError &&
      auditData?.businessUnits &&
      auditData.fromDate &&
      auditData.toDate
    );
  };

  useEffect(() => {
    const allBunitsList =
      localStorage.getItem("businessUnits") &&
      JSON.parse(localStorage.getItem("businessUnits") || "{}");
    if (allBunitsList?.length === 1) {
      setBunitsList([
        { label: allBunitsList[0].name, value: allBunitsList[0].id },
      ]);
    } else if (allBunitsList?.length) {
      const sortedBunits = allBunitsList.map((each: any, index: any) => {
        return {
          ...each,
          label: each.name,
          value: each.id,
        };
      });
      sortedBunits.sort((a: any, b: any) => a.label.localeCompare(b.label));
      setBunitsList([...sortedBunits]);
    }
  }, []);

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col px-8 py-4 overflow-y-auto pw-offset-height">
        <div className="flex pw-input-label pw-create-label">
          {"Create external audit"}
        </div>
        <Form layout="vertical">
          <Form.Item
            label={
              <span>
                Verifying company name{" "}
                <span className="pw-mandatory-icon">*</span>
              </span>
            }
            className="pw-offset-form-field"
          >
            <Input
              placeholder="Enter company name"
              value={auditData.auditorCompany}
              onChange={(e) => handleChange(e.target.value, "auditorCompany")}
              className="h-[2.5rem]"
            />
          </Form.Item>

          <Form.Item
            label={
              <span>
                Email ID <span className="pw-mandatory-icon">*</span>
              </span>
            }
            className="pw-offset-form-field"
          >
            <Input
              placeholder="Enter email id"
              value={auditData.email}
              onChange={(e) => handleChange(e.target.value, "email")}
              className="h-[2.5rem]"
            />
            {emailError ? (
              <span
                className="pw-display-error"
                id="pw-e2e-employee-email-error"
              >
                {emailError}
              </span>
            ) : (
              ""
            )}
          </Form.Item>

          <Form.Item
            label={
              <span>
                Reporting period <span className="pw-mandatory-icon">*</span>
              </span>
            }
            className="pw-offset-form-field"
          >
            <RangePicker
              className="pw-external-audit"
              picker="month"
              onCalendarChange={(val: any) => {
                setDates(val);
              }}
              onChange={onChangeDate}
              onOpenChange={onOpenChange}
              placeholder={["From", "To"]}
              defaultValue={defaultDateRange}
              format={monthFormat}
            />
          </Form.Item>

          <div
            className="pw-linked-businessunit"
            style={{ width: "50rem", height: "100%" }}
          >
            <div className="flex flex-row pw-form-field">
              <div className="pw-input-label pw-text">Link to facility</div>
              <div className="pw-mandatory-icon">*</div>
            </div>
            <Space
              className="space-container"
              style={{ width: "50%", height: "100%" }}
              direction="vertical"
            >
              <AntSelect
                mode="multiple"
                allowClear
                showSearch={false}
                defaultValue={
                  bunitsList?.length > 0
                    ? bunitsList
                    : bunitsList?.length === 1
                    ? bunitsList[0].label
                    : []
                }
                style={{ width: "100%", height: "100%" }}
                placeholder="Select Facilities"
                id="pw-e2e-facilitydropdown"
                onChange={(e) => onChangeBunit(e, "businessUnitIds")}
                options={bunitsList?.length && bunitsList}
              />
            </Space>
          </div>

          {/* <Form.Item
            label={
              <span>
                {"Select facility(s)"}
                <span className="pw-mandatory-icon">*</span>
              </span>
            }
            className="pw-offset-form-field"
          >
            <Space
              className="space-container"
              style={{ width: "100%", height: "100%" }}
              direction="vertical"
            >
              <AntSelect
                mode="multiple"
                allowClear
                showSearch={false}
                defaultValue={
                  bunitsList?.length > 0
                    ? bunitsList
                    : bunitsList?.length === 1
                    ? bunitsList[0].label
                    : []
                }
                style={{ width: "100%", height: "100%" }}
                placeholder="Select facility(s)"
                onChange={(e) => onChangeBunit(e, "businessUnitIds")}
                options={bunitsList?.length && bunitsList}
              />
            </Space>
          </Form.Item> */}
        </Form>
      </div>

      <div className="flex flex-row justify-end pw-bunit-footer w-full gap-6">
        <div
          className="pw-bunit-button-light pw-bunit-button-next text-neutral bg-white font-bold"
          onClick={handleCancel}
        >
          Cancel
        </div>
        <button
          className={`pw-bunit-button-primary pw-bunit-button-next text-white  font-bold ${
            isValidForm()
              ? isLoading
                ? "text-black bg-neutral-300"
                : "cursor-pointer bg-primary"
              : "text-black bg-neutral-300"
          }`}
          onClick={() => {
            isValidForm() && !isLoading && handleSubmit();
          }}
          disabled={!isValidForm() || isLoading}
        >
          {isLoading ? (
            <div className="flex flex-row gap-2 p-2">
              <span>{"Saving up"}</span>
              <CircularProgress size={20} />
            </div>
          ) : (
            <span>{"Save"}</span>
          )}
        </button>
      </div>
    </div>
  );
};

export default AuditCreation;
